// src/utils/csvImport.js
// Parses CSV files for import into ODFTrades
// Supports: ODFTrades format, MT4/MT5 history export

// ── ODFTRADES FORMAT ─────────────────────────────────────────────
const ODF_FIELD_MAP = {
  'date':            'date',
  'pair':            'pair',
  'session':         'session',
  'direction':       'direction',
  'entry_price':     'entryPrice',
  'entry':           'entryPrice',
  'stop_loss':       'stopLoss',
  'sl':              'stopLoss',
  'take_profit':     'takeProfit',
  'tp':              'takeProfit',
  'risk_amount':     'riskAmount',
  'risk':            'riskAmount',
  'lot_size':        'lotSize',
  'lots':            'lotSize',
  'contracts':       'contracts',
  'result':          'result',
  'pnl':             'pnl',
  'profit':          'pnl',
  'p&l ($)':         'pnl',
  'rr_achieved':     'rrAchieved',
  'rr':              'rrAchieved',
  'followed_rules':  'followedRules',
  'rules':           'followedRules',
  'poi':             'poi',
  'strategy / poi':  'poi',
  'strategy':        'poi',
  'order_flow':      'orderFlow',
  'analysis_tf':     'analysisTF',
  'timeframe':       'analysisTF',
  'mistake_made':    'mistakeMade',
  'mistake':         'mistakeMade',
  'emotion_before':  'emotionBefore',
  'emotion_after':   'emotionAfter',
  'notes':           'notes',
  'screenshot_link': 'screenshotLink',
  'screenshot':      'screenshotLink',
};

// ── MT4/MT5 FIELD MAPPINGS ───────────────────────────────────────
const MT4_FIELD_MAP = {
  // Standard MT4 history export fields
  'symbol':          'pair',
  'instrument':      'pair',
  'open time':       'date',
  'open_time':       'date',
  'opentime':        'date',
  'close time':      'closeDate',
  'close_time':      'closeDate',
  'type':            'direction',
  'direction':       'direction',
  'volume':          'lotSize',
  'lots':            'lotSize',
  'open price':      'entryPrice',
  'open_price':      'entryPrice',
  'openprice':       'entryPrice',
  'close price':     'pnlExitPrice',
  'close_price':     'pnlExitPrice',
  'closeprice':      'pnlExitPrice',
  's/l':             'stopLoss',
  'sl':              'stopLoss',
  'stop loss':       'stopLoss',
  't/p':             'takeProfit',
  'tp':              'takeProfit',
  'take profit':     'takeProfit',
  'profit':          'pnl',
  'net profit':      'pnl',
  'commission':      'commission',
  'swap':            'swap',
  'comment':         'notes',
  'magic':           null, // ignore
  'ticket':          null, // ignore
};

// ── PARSE CSV STRING ─────────────────────────────────────────────
export function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  // Parse headers
  const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());

  // Parse rows
  const rows = lines.slice(1)
    .filter(l => l.trim())
    .map(line => {
      const cells = parseCSVLine(line);
      const row = {};
      headers.forEach((h, i) => { row[h] = cells[i]?.trim() || ''; });
      return row;
    })
    .filter(row => Object.values(row).some(v => v !== ''));

  return { headers, rows };
}

function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (c === ',' && !inQuotes) {
      result.push(current); current = '';
    } else {
      current += c;
    }
  }
  result.push(current);
  return result;
}

// ── DETECT FORMAT ────────────────────────────────────────────────
export function detectFormat(headers) {
  const mt4Keys = ['symbol', 'open time', 'open price', 'close price', 'volume', 'profit'];
  const mt4Matches = mt4Keys.filter(k => headers.includes(k)).length;
  if (mt4Matches >= 3) return 'mt4';

  const odfKeys = ['date', 'pair', 'result', 'pnl'];
  const odfMatches = odfKeys.filter(k => headers.includes(k)).length;
  if (odfMatches >= 2) return 'odftrades';

  return 'unknown';
}

// ── MAP ODF ROW TO TRADE ─────────────────────────────────────────
export function mapODFRow(row) {
  const trade = {};
  for (const [csvKey, tradeKey] of Object.entries(ODF_FIELD_MAP)) {
    if (row[csvKey] !== undefined && tradeKey) {
      trade[tradeKey] = row[csvKey];
    }
  }
  return normalizeTrade(trade);
}

// ── MAP MT4 ROW TO TRADE ─────────────────────────────────────────
export function mapMT4Row(row) {
  const trade = {};
  for (const [csvKey, tradeKey] of Object.entries(MT4_FIELD_MAP)) {
    if (row[csvKey] !== undefined && tradeKey) {
      trade[tradeKey] = row[csvKey];
    }
  }

  // MT4-specific transformations
  // Direction: 'buy' → 'Buy', 'sell' → 'Sell', '0' → 'Buy', '1' → 'Sell'
  if (trade.direction) {
    const d = String(trade.direction).toLowerCase().trim();
    if (d === 'buy' || d === '0' || d === 'buy limit' || d === 'buy stop') trade.direction = 'Buy';
    else if (d === 'sell' || d === '1' || d === 'sell limit' || d === 'sell stop') trade.direction = 'Sell';
  }

  // Date: MT4 uses "YYYY.MM.DD HH:MM" or "YYYY-MM-DD HH:MM:SS"
  if (trade.date) {
    trade.date = parseMT4Date(trade.date);
  }

  // Result: derive from profit
  const pnl = parseFloat(trade.pnl || 0);
  if (!trade.result) {
    trade.result = pnl > 0 ? 'Win' : pnl < 0 ? 'Loss' : 'Breakeven';
  }

  // Include commission and swap in PnL if available
  const commission = parseFloat(trade.commission || 0);
  const swap       = parseFloat(trade.swap || 0);
  trade.pnl        = (pnl + commission + swap).toFixed(2);
  delete trade.commission;
  delete trade.swap;
  delete trade.pnlExitPrice;
  delete trade.closeDate;

  return normalizeTrade(trade);
}

function parseMT4Date(dateStr) {
  if (!dateStr) return '';
  // "YYYY.MM.DD HH:MM" → "YYYY-MM-DD"
  const dotFormat = dateStr.match(/^(\d{4})\.(\d{2})\.(\d{2})/);
  if (dotFormat) return `${dotFormat[1]}-${dotFormat[2]}-${dotFormat[3]}`;
  // "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DD"
  const dashFormat = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dashFormat) return dashFormat[1];
  return dateStr.slice(0, 10);
}

// ── NORMALIZE TRADE ──────────────────────────────────────────────
function normalizeTrade(trade) {
  return {
    date:           trade.date           || '',
    pair:           trade.pair           || '',
    session:        trade.session        || '',
    direction:      capitalize(trade.direction || ''),
    entryPrice:     parseFloatSafe(trade.entryPrice),
    stopLoss:       parseFloatSafe(trade.stopLoss),
    takeProfit:     parseFloatSafe(trade.takeProfit),
    riskAmount:     parseFloatSafe(trade.riskAmount),
    lotSize:        parseFloatSafe(trade.lotSize),
    contracts:      parseFloatSafe(trade.contracts),
    result:         capitalizeResult(trade.result || ''),
    pnl:            parseFloatSafe(trade.pnl),
    rrAchieved:     parseFloatSafe(trade.rrAchieved),
    followedRules:  parseBool(trade.followedRules),
    poi:            trade.poi            || '',
    orderFlow:      trade.orderFlow      || '',
    analysisTF:     trade.analysisTF     || '',
    mistakeMade:    trade.mistakeMade    || 'None',
    emotionBefore:  capitalize(trade.emotionBefore || 'Calm'),
    emotionAfter:   capitalize(trade.emotionAfter  || 'Calm'),
    notes:          trade.notes          || '',
    screenshotLink: trade.screenshotLink || '',
  };
}

function parseFloatSafe(val) {
  if (val === '' || val === undefined || val === null) return null;
  const n = parseFloat(String(val).replace(/[^0-9.\-]/g, ''));
  return isNaN(n) ? null : n;
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function capitalizeResult(str) {
  const s = str.toLowerCase().trim();
  if (s === 'win' || s === 'won' || s === '1') return 'Win';
  if (s === 'loss' || s === 'lost' || s === '-1') return 'Loss';
  if (s === 'breakeven' || s === 'be' || s === '0') return 'Breakeven';
  return str || 'Loss';
}

function parseBool(val) {
  if (typeof val === 'boolean') return val;
  const s = String(val || '').toLowerCase().trim();
  return s === 'yes' || s === 'true' || s === '1';
}

// ── VALIDATE TRADE ───────────────────────────────────────────────
export function validateTrade(trade, index) {
  const errors = [];
  if (!trade.date || !trade.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
    errors.push(`Row ${index + 1}: Invalid or missing date (expected YYYY-MM-DD)`);
  }
  if (!trade.pair) {
    errors.push(`Row ${index + 1}: Missing pair/instrument`);
  }
  if (!trade.result || !['Win','Loss','Breakeven'].includes(trade.result)) {
    errors.push(`Row ${index + 1}: Invalid result (must be Win, Loss, or Breakeven)`);
  }
  return errors;
}

// ── DEDUPLICATE ──────────────────────────────────────────────────
export function deduplicateTrades(incoming, existing) {
  const existingKeys = new Set(
    existing.map(t => `${t.date}_${t.pair}_${t.direction}_${t.entryPrice}`)
  );
  const unique  = [];
  const skipped = [];
  for (const t of incoming) {
    const key = `${t.date}_${t.pair}_${t.direction}_${t.entryPrice}`;
    if (existingKeys.has(key)) skipped.push(t);
    else unique.push(t);
  }
  return { unique, skipped };
}

// ── FLEXIBLE COLUMN MAPPER (for unknown formats) ─────────────────
export function buildColumnMapper(headers) {
  // Returns suggested mappings for unknown CSV formats
  const odfFields = [
    { key: 'date',          label: 'Date',          required: true  },
    { key: 'pair',          label: 'Pair',          required: true  },
    { key: 'direction',     label: 'Direction',     required: false },
    { key: 'entryPrice',    label: 'Entry Price',   required: false },
    { key: 'stopLoss',      label: 'Stop Loss',     required: false },
    { key: 'takeProfit',    label: 'Take Profit',   required: false },
    { key: 'result',        label: 'Result',        required: true  },
    { key: 'pnl',           label: 'P&L',           required: false },
    { key: 'rrAchieved',    label: 'RR',            required: false },
    { key: 'lotSize',       label: 'Lot Size',      required: false },
    { key: 'notes',         label: 'Notes',         required: false },
  ];

  const suggestions = {};
  for (const field of odfFields) {
    // Auto-suggest by fuzzy matching
    const match = headers.find(h => {
      const hNorm = h.toLowerCase().replace(/[^a-z]/g, '');
      const kNorm = field.key.toLowerCase().replace(/[^a-z]/g, '');
      return hNorm === kNorm || hNorm.includes(kNorm) || kNorm.includes(hNorm);
    });
    suggestions[field.key] = match || '';
  }

  return { fields: odfFields, suggestions };
}
